import { useEffect, useState } from "react";

import * as yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import PropTypes from "prop-types";
import { App, Typography } from "antd";
import { useSelector } from "react-redux";
import { skipToken } from "@reduxjs/toolkit/query";

import PrimaryButton from "../../../components/PrimaryButton.jsx";
import {
  useCreateCommentMutation,
  useGetDiscussionByIdQuery,
  useGetTopicsQuery,
  useUpdateCommentMutation,
} from "../../../redux/service.js";
import ContentField from "./ContentField.jsx";
import UserAvatar from "../../../domains/users/components/UserAvatar.tsx";
import { selectCurrentUser } from "@/redux/authentication/selectors.js";
import { MediaUploadProvider, useMediaUploadGate } from "@/shared/media";
import { handleErrors } from "@/shared/utils/forms";

const { Text } = Typography;

const CommentSubmitButton = ({ onClick }) => {
  const hasUploads = useMediaUploadGate();
  const { t } = useTranslation();
  return (
    <PrimaryButton onClick={onClick} disabled={hasUploads}>
      {t("buttons.post")}
    </PrimaryButton>
  );
};

CommentSubmitButton.propTypes = {
  onClick: PropTypes.func.isRequired,
};

const CommentFormInner = ({
  initialData = null,
  hideCommentBox = () => {},
  submitCommentCallback = () => {},
}) => {
  const { t } = useTranslation();
  const { discussionId } = useParams();
  const { notification } = App.useApp();

  const { data: discussion } = useGetDiscussionByIdQuery(
    discussionId ?? skipToken,
  );
  const { data: discussionTopics } = useGetTopicsQuery(
    discussion ? { ids: discussion.topics, pagination: "off" } : skipToken,
  );
  const isInCertificationTopic =
    Array.isArray(discussionTopics) &&
    discussionTopics.some((topic) => topic.isCertification);

  const [createComment] = useCreateCommentMutation();
  const [updateComment] = useUpdateCommentMutation();
  const currentUser = useSelector(selectCurrentUser);
  const [showCommentForm, setShowCommentForm] = useState(initialData?.content);

  const commentSchema = yup.object().shape({
    content: yup
      .string()
      .typeError(t("form.validation.commentNotValid"))
      .required(t("form.validation.commentRequired")),
    blocks: yup.array(),
    surveyViews: yup.array(),
  });

  const defaultValues = {
    parent: null,
    discussion: discussionId,
    content: "",
    blocks: [],
    surveyViews: [],
  };
  const form = useForm({
    resolver: yupResolver(commentSchema),
    mode: "onChange",
    defaultValues,
  });

  useEffect(() => {
    if (initialData) {
      form.reset({
        parent: initialData?.parent,
        discussion: initialData?.discussion || discussionId,
        content: initialData?.content,
        blocks: initialData?.attachedBlocks,
        surveyViews: initialData?.surveyViews,
      });
    }
  }, [initialData]);

  const cancelComment = () => {
    form.reset(defaultValues);
    hideCommentBox();
  };

  const cleanUpAfterRequest = () => {
    setShowCommentForm(false);
  };

  const handleCommentSubmit = (values) => {
    const data = {
      ...values,
      attachedBlockIds: (values?.blocks || []).map((i) => i.id),
      attachedSurveyViewUuids: (values?.surveyViews || []).map((i) => i.id),
    };
    const onError = (errors) =>
      handleErrors(errors, form.setError, notification, t);
    if (initialData?.id) {
      updateComment({ id: initialData.id, data })
        .unwrap()
        .then((data) => {
          submitCommentCallback(data);
          form.reset(defaultValues);
          hideCommentBox();
        })
        .catch(onError)
        .finally(cleanUpAfterRequest);
    } else {
      createComment(data)
        .unwrap()
        .then((data) => {
          submitCommentCallback(data);
          form.reset(defaultValues);
          hideCommentBox();
          notification.success({ message: t("newCommentCreated") });
        })
        .catch(onError)
        .finally(cleanUpAfterRequest);
    }
  };

  const handleShowInput = () => {
    setShowCommentForm(true);
  };

  return showCommentForm ? (
    <ContentField form={form} allowAttachments={!isInCertificationTopic}>
      <PrimaryButton type="tertiary" onClick={cancelComment} className="me-3">
        {t("buttons.cancel")}
      </PrimaryButton>
      <CommentSubmitButton onClick={form.handleSubmit(handleCommentSubmit)} />
    </ContentField>
  ) : (
    <div
      style={{
        border: "1px solid rgba(185, 187, 198, 1)",
        alignItems: "stretch",
        display: "flex",
      }}
    >
      <div className="py-2 px-2">
        {currentUser && <UserAvatar user={currentUser} />}
      </div>
      <div
        style={{ cursor: "text", display: "flex", alignItems: "center" }}
        onClick={handleShowInput}
      >
        <Text style={{ color: "rgba(139, 141, 152, 1)" }} className="ms-2">
          {t("form.placeholders.discussionCommentForm")}
        </Text>
      </div>
      <div style={{ margin: "0 0 0 auto" }} className="py-2 px-2">
        <PrimaryButton disabled>{t("buttons.postComment")}</PrimaryButton>
      </div>
    </div>
  );
};

CommentFormInner.propTypes = {
  initialData: PropTypes.object,
  hideCommentBox: PropTypes.func,
  submitCommentCallback: PropTypes.func,
};

const CommentForm = (props) => (
  <MediaUploadProvider>
    <CommentFormInner {...props} />
  </MediaUploadProvider>
);

CommentForm.propTypes = CommentFormInner.propTypes;

export default CommentForm;
